import { useState, type FormEvent } from 'react'
import PageHeader from '../components/PageHeader'
import { asset, pages } from '../data/content'

export default function Contact() {
  const { kicker, title, dek, channels } = pages.contact
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    const payload = {
      name: String(form.get('name') || ''),
      email: String(form.get('email') || ''),
      message: String(form.get('message') || ''),
      website: String(form.get('website') || ''),
    }
    setBusy(true)
    setError('')
    try {
      const res = await fetch('/api/public/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const data = (await res.json().catch(() => null)) as { error?: string } | null
      if (!res.ok) throw new Error(data?.error || 'Could not send your note.')
      setSent(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send your note.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="py-[clamp(28px,4.5vw,60px)]">
      <div className="grid grid-cols-1 gap-[clamp(32px,5vw,72px)] lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:items-start">
        <div>
          <PageHeader kicker={kicker} title={title} dek={dek} />

          <div data-reveal className="mt-12 space-y-8 border-t border-rule pt-8">
            {channels.map((channel) => (
              <div key={channel.email}>
                <p className="text-caps m-0 text-muted">{channel.label}</p>
                <a
                  href={`mailto:${channel.email}`}
                  className="text-caps-h3 mt-2 inline-block text-rose hover:text-ink"
                >
                  {channel.email}
                </a>
              </div>
            ))}
          </div>
        </div>

        <div data-reveal className="flex flex-col gap-8">
          <div className="hidden lg:block">
            <img
              src={asset('2022/01/sky-haarsma-2-1.jpg')}
              alt="Sky Haarsma"
              className="aspect-[4/5] w-full max-w-[260px] object-cover object-top bg-sand"
            />
          </div>

          <div className="border border-rule bg-paper p-[clamp(20px,3vw,32px)]">
            {sent ? (
              <p className="text-caps-h3 m-0 text-ink">Got it. Talk soon.</p>
            ) : (
              <form onSubmit={(event) => void onSubmit(event)} className="space-y-6">
                <p className="text-caps m-0 text-muted">Send a note</p>
                <label className="sr-only" aria-hidden="true">
                  Website
                  <input name="website" tabIndex={-1} autoComplete="off" />
                </label>
                <label className="block">
                  <span className="text-caps mb-2 block text-muted">Name</span>
                  <input
                    required
                    name="name"
                    autoComplete="name"
                    className="h-12 w-full border border-rule bg-sand px-4 text-sm outline-none focus:border-rose"
                  />
                </label>
                <label className="block">
                  <span className="text-caps mb-2 block text-muted">Email</span>
                  <input
                    type="email"
                    name="email"
                    required
                    autoComplete="email"
                    className="h-12 w-full border border-rule bg-sand px-4 text-sm outline-none focus:border-rose"
                  />
                </label>
                <label className="block">
                  <span className="text-caps mb-2 block text-muted">Message</span>
                  <textarea
                    required
                    name="message"
                    rows={6}
                    className="w-full border border-rule bg-sand px-4 py-3 text-sm outline-none focus:border-rose"
                  />
                </label>
                {error ? <p className="text-sm text-rose">{error}</p> : null}
                <button type="submit" disabled={busy} className="text-caps h-12 bg-ink px-8 text-white hover:bg-rose disabled:opacity-60">
                  {busy ? 'Sending' : 'Send'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </main>
  )
}
